// src/components/AccountSwitcher.js — One-click switch between Travis and the
// Jean's partner login (sidebar circles, both the CRM and the portal).
//
// A switch is a real sign-in as the other user: the API mints a one-time
// token (src/controllers/accountSwitchController.js), verifyOtp exchanges it
// for that user's own session, and a full page load lands on that user's side
// (/portal for the partner, / for staff) with no state left from the previous
// account. Renders nothing for anyone the API doesn't allow to switch.
//
// Switching works for 1 hour after Travis's password sign-in. The signed
// grant is kept in localStorage; once a switch has happened, reaching its
// expiry signs the browser out entirely.
import { useEffect, useState } from 'react'
import supabase from '../lib/supabase'
import api from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { readSwitchGrant as readStored, writeSwitchGrant as writeStored, clearSwitchGrant } from '../lib/accountSwitchGrant'

async function expireAndSignOut() {
  clearSwitchGrant()
  try { await supabase.auth.signOut({ scope: 'local' }) } catch {}
  window.location.assign('/login')
}

function initials(name, email) {
  const words = (name || email || '').split(/[\s_.@-]+/).filter(w => /[a-z]/i.test(w))
  return words.slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?'
}

export default function AccountSwitcher({ collapsed }) {
  const { user } = useAuth()
  const [accounts, setAccounts] = useState(null)
  const [busy, setBusy] = useState(null)   // email being switched to
  const [error, setError] = useState(null)

  // Load the switchable accounts for whoever is signed in now.
  useEffect(() => {
    if (!user) return
    const stored = readStored()
    if (stored && stored.expires_at <= Date.now() / 1000) { expireAndSignOut(); return }

    let cancelled = false
    api.get('/api/auth/switch-accounts', { headers: stored ? { 'x-switch-grant': stored.grant } : {} })
      .then(res => { if (!cancelled) setAccounts(res.data.data.accounts) })
      .catch(() => {
        if (cancelled) return
        setAccounts(null)
        // A stored grant the API no longer accepts: the window is over.
        if (stored) expireAndSignOut()
      })
    return () => { cancelled = true }
  }, [user])

  // Once a switch has happened, sign out when the 1-hour window ends.
  useEffect(() => {
    const stored = readStored()
    if (!stored) return
    const ms = stored.expires_at * 1000 - Date.now()
    const t = setTimeout(expireAndSignOut, Math.max(ms, 0))
    return () => clearTimeout(t)
  }, [user])

  async function switchTo(account) {
    if (busy || account.current) return
    setBusy(account.email)
    setError(null)
    try {
      const stored = readStored()
      const res = await api.post('/api/auth/switch-account', { email: account.email },
        { headers: stored ? { 'x-switch-grant': stored.grant } : {} })
      const { token_hash, role, grant, expires_at } = res.data.data
      writeStored({ grant, expires_at })

      const { error: otpError } = await supabase.auth.verifyOtp({ token_hash, type: 'magiclink' })
      if (otpError) throw otpError

      // Full load so nothing cached from the previous account survives.
      window.location.assign(role === 'partner' ? '/portal' : '/')
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not switch accounts')
      setBusy(null)
    }
  }

  if (!accounts || accounts.length < 2) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: collapsed ? 'center' : 'flex-start' }}>
      <div style={{ display: 'flex', flexDirection: collapsed ? 'column' : 'row', gap: '8px' }}>
        {accounts.map(account => {
          const label = `${account.name || account.email} (${account.role === 'partner' ? 'partner portal' : 'RGA CRM'})`
          return (
            <button
              key={account.id}
              onClick={() => switchTo(account)}
              disabled={!!busy}
              title={account.current ? `Signed in as ${label}` : `Switch to ${label}`}
              aria-label={account.current ? `Signed in as ${label}` : `Switch to ${label}`}
              aria-pressed={account.current}
              style={{
                width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 600, letterSpacing: '0.02em', fontFamily: 'inherit',
                cursor: account.current ? 'default' : (busy ? 'wait' : 'pointer'),
                background: account.current ? 'var(--accent, #3b82f6)' : 'transparent',
                color: account.current ? '#fff' : 'var(--text-secondary, #9ca3af)',
                border: account.current ? '2px solid var(--accent, #3b82f6)' : '1px solid var(--border, #1f2937)',
                boxShadow: account.current ? '0 0 0 2px var(--accent-muted, rgba(59,130,246,0.25))' : 'none',
                opacity: busy === account.email ? 0.6 : 1,
                transition: 'background 0.12s, color 0.12s, border-color 0.12s',
              }}
              onMouseEnter={e => {
                if (account.current) return
                e.currentTarget.style.borderColor = 'var(--accent, #3b82f6)'
                e.currentTarget.style.color = 'var(--text-primary, #e5e7eb)'
              }}
              onMouseLeave={e => {
                if (account.current) return
                e.currentTarget.style.borderColor = 'var(--border, #1f2937)'
                e.currentTarget.style.color = 'var(--text-secondary, #9ca3af)'
              }}
            >
              {initials(account.name, account.email)}
            </button>
          )
        })}
      </div>
      {error && !collapsed && (
        <div style={{ fontSize: '11px', color: 'var(--danger-text, #f87171)' }}>{error}</div>
      )}
    </div>
  )
}
